// PlaceWizard - Multi-Step Place Creation Form
// UI/UX Redesign: Draft-Save, Success-Screen, Validierung, Kartenintegration

import React, { useState, useEffect } from 'react';
import { ChevronLeft, Check, PartyPopper, MapPin, ArrowRight } from 'lucide-react';
import type {
  MainCategory,
  GoodWorkCategory,
  MoodLabel,
  ActivityLabel,
  CommunityFeature,
  PlaceCreateInput,
  OpeningHours,
  ValouAreaId,
  VibeTag,
  PlaceVisibility,
  Realm,
  PlaceAmenities,
  PlacePricing,
  PlaceWorkspaces,
  PlaceGoodworkEvents,
} from '../../../types';
import { MAIN_CATEGORIES, GOODWORK_CATEGORIES, ALL_CATEGORIES } from '../../../types';
import { StepNameCategory } from './StepNameCategory';
import { StepLocation } from './StepLocation';
import { StepDescription } from './StepDescription';
import { StepDetails } from './StepDetails';
import { StepVisibility } from './StepVisibility';
import { StepGoodWork } from './StepGoodWork';

interface PlaceWizardProps {
  onSubmit: (data: PlaceCreateInput) => Promise<void>;
  onCancel: () => void;
}

interface PlaceFormData {
  // Step 1: Name, Realms & Kategorien
  name: string;
  realms: Realm[];                    // Multi-select: ['goodlife'], ['goodwork'], oder ['goodlife', 'goodwork']
  category: MainCategory | '';         // GoodLife-Kategorie
  goodworkCategory: GoodWorkCategory | ''; // GoodWork-Kategorie

  // Step 2: Standort (Koordinaten Pflicht, Adresse optional)
  latitude: number | null;
  longitude: number | null;
  address: string;
  postalCode: string;
  city: string;

  // Step 3: Beschreibung
  tagline: string;
  goodFactor: string;
  pitch: string;
  description: string;

  // Step 4: Details
  images: string[];
  vibeTags: VibeTag[];
  moods: MoodLabel[];
  activities: ActivityLabel[];
  community: CommunityFeature[];
  valouAreas: ValouAreaId[];
  openingHours: OpeningHours;
  website: string;
  phone: string;
  email: string;
  showSubmitter: boolean;

  // Step 5: Sichtbarkeit
  visibility: PlaceVisibility;

  // GoodWork-spezifische Felder (Step 3.5 - nur bei GoodWork)
  goodworkFactor: string;
  amenities: PlaceAmenities;
  pricing: PlacePricing;
  workspaces: PlaceWorkspaces;
  goodworkEvents: PlaceGoodworkEvents;
}

const INITIAL_FORM_DATA: PlaceFormData = {
  // Step 1: Name, Realms & Kategorien
  name: '',
  realms: ['goodlife'],      // Default: nur GoodLife
  category: '',
  goodworkCategory: '',
  // Step 2: Standort
  latitude: null,
  longitude: null,
  address: '',
  postalCode: '',
  city: '',
  // Step 3: Beschreibung
  tagline: '',
  goodFactor: '',
  pitch: '',
  description: '',
  // Step 4: Details
  images: [],
  vibeTags: [],
  moods: [],
  activities: [],
  community: [],
  valouAreas: [],
  openingHours: {
    displayText: '',
  },
  website: '',
  phone: '',
  email: '',
  showSubmitter: false,
  // Step 5: Sichtbarkeit
  visibility: 'public',
  // GoodWork-spezifische Felder
  goodworkFactor: '',
  amenities: {},
  pricing: {},
  workspaces: {},
  goodworkEvents: {},
};

const DRAFT_STORAGE_KEY = 'placeWizardDraft';

// Base steps without GoodWork
const BASE_STEPS = [
  { id: 1, title: 'Name & Kategorie', description: 'Grundlegende Infos' },
  { id: 2, title: 'Standort', description: 'Koordinaten erfassen' },
  { id: 3, title: 'Beschreibung', description: 'Was macht ihn besonders?' },
  { id: 4, title: 'Details', description: 'Bilder, Stimmung & mehr' },
  { id: 5, title: 'Sichtbarkeit', description: 'Privat oder oeffentlich?' },
];

// GoodWork step inserted after Beschreibung
const GOODWORK_STEP = { id: 3.5, title: 'GoodWork Details', description: 'Ausstattung & Preise' };

// Helper to get steps based on whether GoodWork is selected
const getSteps = (hasGoodWork: boolean) => {
  if (!hasGoodWork) return BASE_STEPS;
  // Insert GoodWork step after step 3 (Beschreibung)
  return [
    BASE_STEPS[0],
    BASE_STEPS[1],
    BASE_STEPS[2],
    GOODWORK_STEP,
    { ...BASE_STEPS[3], id: 4 },
    { ...BASE_STEPS[4], id: 5 },
  ];
};

export function PlaceWizard({ onSubmit, onCancel }: PlaceWizardProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<PlaceFormData>(INITIAL_FORM_DATA);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [draftRestored, setDraftRestored] = useState(false);

  // Determine if GoodWork is selected
  const hasGoodWork = formData.realms.includes('goodwork');
  const STEPS = getSteps(hasGoodWork);
  const totalSteps = STEPS.length;
  const currentStepIndex = STEPS.findIndex(s => s.id === currentStep);
  const isLastStep = currentStepIndex === STEPS.length - 1;

  // Restore draft on mount
  useEffect(() => {
    try {
      const savedDraft = localStorage.getItem(DRAFT_STORAGE_KEY);
      if (savedDraft) {
        const parsed = JSON.parse(savedDraft);
        setFormData(parsed);
        setDraftRestored(true);
        // Auto-dismiss after 3 seconds
        setTimeout(() => setDraftRestored(false), 3000);
      }
    } catch (e) {
      console.error('Failed to restore draft:', e);
    }
  }, []);

  // Auto-save draft on changes
  useEffect(() => {
    // Don't save if form is empty or if we just showed success
    if (showSuccess) return;
    const hasData = formData.name || formData.address || formData.category;
    if (hasData) {
      try {
        localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(formData));
      } catch (e) {
        console.error('Failed to save draft:', e);
      }
    }
  }, [formData, showSuccess]);

  // Clear draft after successful submit
  const clearDraft = () => {
    try {
      localStorage.removeItem(DRAFT_STORAGE_KEY);
    } catch (e) {
      console.error('Failed to clear draft:', e);
    }
  };

  const updateFormData = (updates: Partial<PlaceFormData>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  };

  const handleNext = () => {
    const nextStepIndex = currentStepIndex + 1;
    if (nextStepIndex < STEPS.length) {
      setCurrentStep(STEPS[nextStepIndex].id);
    }
  };

  const handleBack = () => {
    const prevStepIndex = currentStepIndex - 1;
    if (prevStepIndex >= 0) {
      setCurrentStep(STEPS[prevStepIndex].id);
    } else {
      onCancel();
    }
  };

  const handleSubmit = async () => {
    if (!formData.category) {
      setError('Bitte wähle eine Kategorie aus');
      return;
    }

    // Validate email if provided
    if (formData.email && !isValidEmail(formData.email)) {
      setError('Bitte gib eine gültige E-Mail-Adresse ein');
      return;
    }

    // Validate website if provided
    if (formData.website && !isValidWebsite(formData.website)) {
      setError('Website muss mit http:// oder https:// beginnen');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Build placeData - Koordinaten sind jetzt Pflicht, Adresse optional
      const placeData: PlaceCreateInput = {
        name: formData.name,
        realms: formData.realms,
        category: formData.category as MainCategory,
        tagline: formData.tagline,
        moods: formData.moods,
        activities: formData.activities,
        community: formData.community,
        openingHours: formData.openingHours,
        latitude: formData.latitude!,
        longitude: formData.longitude!,
        visibility: formData.visibility,
      };

      // GoodWork-Kategorie nur hinzufügen wenn GoodWork im Realm
      if (formData.realms.includes('goodwork') && formData.goodworkCategory) {
        placeData.goodworkCategory = formData.goodworkCategory as GoodWorkCategory;
      }

      // Optionale Felder nur hinzufügen wenn vorhanden
      if (formData.address) {
        placeData.address = formData.address;
      }
      if (formData.postalCode) {
        placeData.postalCode = formData.postalCode;
      }
      if (formData.city) {
        placeData.city = formData.city;
      }
      if (formData.description) {
        placeData.description = formData.description;
      }
      if (formData.images.length > 0) {
        placeData.imageUrl = formData.images[0];
        placeData.images = formData.images;
      }
      if (formData.vibeTags.length > 0) {
        placeData.vibeTags = formData.vibeTags;
      }
      if (formData.website) {
        placeData.website = formData.website;
      }
      if (formData.phone) {
        placeData.phone = formData.phone;
      }
      if (formData.email) {
        placeData.email = formData.email;
      }
      if (formData.valouAreas.length > 0) {
        placeData.valouAreas = formData.valouAreas;
      }
      if (formData.goodFactor) {
        placeData.goodFactor = formData.goodFactor;
      }
      if (formData.pitch) {
        placeData.pitch = formData.pitch;
      }
      if (formData.showSubmitter) {
        placeData.showSubmitter = formData.showSubmitter;
      }

      // GoodWork-spezifische Felder
      if (hasGoodWork) {
        if (formData.goodworkFactor) {
          placeData.goodworkFactor = formData.goodworkFactor;
        }
        if (Object.keys(formData.amenities).length > 0) {
          placeData.amenities = formData.amenities;
        }
        if (Object.keys(formData.pricing).length > 0) {
          placeData.pricing = formData.pricing;
        }
        if (Object.keys(formData.workspaces).length > 0) {
          placeData.workspaces = formData.workspaces;
        }
        if (Object.keys(formData.goodworkEvents).length > 0) {
          placeData.goodworkEvents = formData.goodworkEvents;
        }
      }

      await onSubmit(placeData);
      // Success! Clear draft and show success screen
      clearDraft();
      setShowSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle adding another place
  const handleAddAnother = () => {
    setShowSuccess(false);
    setFormData(INITIAL_FORM_DATA);
    setCurrentStep(1);
    setError(null);
  };

  // Validate German postal code (5 digits)
  const isValidPostalCode = (plz: string): boolean => {
    return /^\d{5}$/.test(plz);
  };

  // Validate email format
  const isValidEmail = (email: string): boolean => {
    if (!email) return true; // optional
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  // Validate website URL
  const isValidWebsite = (url: string): boolean => {
    if (!url) return true; // optional
    return /^https?:\/\/.+/.test(url);
  };

  const isStepValid = (step: number): boolean => {
    switch (step) {
      case 1:
        // Name und passende Kategorien sind Pflicht
        const hasName = !!formData.name;
        const hasRealms = formData.realms.length > 0;
        const hasGoodLifeCat = !formData.realms.includes('goodlife') || !!formData.category;
        const hasGoodWorkCat = !formData.realms.includes('goodwork') || !!formData.goodworkCategory;
        return hasName && hasRealms && hasGoodLifeCat && hasGoodWorkCat;
      case 2:
        // Nur Koordinaten sind Pflicht, Adresse ist optional
        return !!(formData.latitude !== null && formData.longitude !== null);
      case 3:
        // Nur Tagline ist Pflicht
        return !!(formData.tagline);
      case 3.5:
        // GoodWork Details sind alle optional
        return true;
      case 4:
        // Mindestens ein Mood muss ausgewaehlt sein
        return formData.moods.length > 0;
      case 5:
        // Sichtbarkeit hat immer einen gueltigen Default-Wert
        return true;
      default:
        return false;
    }
  };

  // Show Success Screen
  if (showSuccess) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-6">
        <div className="max-w-md w-full text-center">
          {/* Success Icon */}
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <PartyPopper className="w-10 h-10 text-green-600" />
          </div>

          {/* Title */}
          <h1 className="text-2xl font-bold text-gray-900 mb-3">
            Vielen Dank!
          </h1>

          {/* Description */}
          <p className="text-gray-600 mb-2">
            {formData.visibility === 'private' ? (
              <>Dein privater GoodPlace <strong>{formData.name}</strong> wurde erstellt!</>
            ) : (
              <>Dein Vorschlag fuer <strong>{formData.name}</strong> wurde erfolgreich eingereicht.</>
            )}
          </p>
          <p className="text-gray-500 text-sm mb-8">
            {formData.visibility === 'private' ? (
              <>Der Ort ist jetzt fuer dich sichtbar. Du kannst ihn jederzeit mit Freunden teilen oder oeffentlich machen.</>
            ) : (
              <>Unser Team prueft den Ort und schaltet ihn innerhalb von 24 Stunden frei. Du erhaeltst eine Benachrichtigung, sobald der Ort live ist.</>
            )}
          </p>

          {/* Place Preview */}
          <div className="bg-gray-50 rounded-xl p-4 mb-8 text-left">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-indigo-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <MapPin className="w-5 h-5 text-indigo-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-gray-900 truncate">{formData.name}</h3>
                <p className="text-sm text-gray-500 truncate">
                  {formData.address && formData.city
                    ? `${formData.address}, ${formData.city}`
                    : formData.city || formData.address || 'Standort erfasst'}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  {ALL_CATEGORIES.find(c => c.id === formData.category)?.label || formData.category}
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleAddAnother}
              className="flex-1 px-6 py-3 border-2 border-indigo-500 text-indigo-600 font-semibold rounded-lg hover:bg-indigo-500/5 transition flex items-center justify-center gap-2"
            >
              Weiteren Ort vorschlagen
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={onCancel}
              className="flex-1 px-6 py-3 bg-indigo-500 text-white font-semibold rounded-lg hover:bg-indigo-600 transition"
            >
              Zur Uebersicht
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Draft Restored Toast */}
      {draftRestored && (
        <div className="fixed top-4 right-4 z-50 bg-gray-900 text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 animate-fade-in">
          <Check className="w-4 h-4 text-green-400" />
          <span className="text-sm">Entwurf wiederhergestellt</span>
        </div>
      )}

      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button
              onClick={handleBack}
              className="p-2 hover:bg-gray-100 rounded-full transition"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <h1 className="font-semibold text-gray-900">Ort vorschlagen</h1>
            <div className="w-9" /> {/* Spacer for alignment */}
          </div>
        </div>
      </header>

      {/* Progress Bar - Redesigned for better mobile UX */}
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          {/* Mobile: Show current step info */}
          <div className="sm:hidden mb-3 text-center">
            <p className="text-sm text-gray-500">
              Schritt {currentStepIndex + 1} von {totalSteps}
            </p>
            <p className="font-semibold text-gray-900">
              {STEPS[currentStepIndex]?.title}
            </p>
          </div>

          {/* Progress dots/steps */}
          <div className="flex items-center justify-between">
            {STEPS.map((step, index) => (
              <React.Fragment key={step.id}>
                <div className="flex items-center">
                  {/* Step Circle */}
                  <div
                    className={`relative w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-300 ${
                      currentStepIndex > index
                        ? 'bg-indigo-500 text-white'
                        : currentStepIndex === index
                        ? 'bg-indigo-500 text-white ring-4 ring-primary/20'
                        : 'bg-gray-200 text-gray-600'
                    }`}
                  >
                    {currentStepIndex > index ? (
                      <Check className="w-5 h-5" />
                    ) : (
                      index + 1
                    )}
                    {/* Active step pulse animation */}
                    {currentStep === step.id && (
                      <span className="absolute inset-0 rounded-full bg-indigo-500 animate-ping opacity-25" />
                    )}
                  </div>

                  {/* Step label - visible on larger screens */}
                  <div className="ml-3 hidden sm:block">
                    <p className={`text-sm font-medium ${
                      currentStepIndex >= index ? 'text-gray-900' : 'text-gray-500'
                    }`}>
                      {step.title}
                    </p>
                    <p className="text-xs text-gray-500">{step.description}</p>
                  </div>
                </div>

                {/* Connector line */}
                {index < STEPS.length - 1 && (
                  <div className="flex-1 mx-2 sm:mx-4">
                    <div
                      className={`h-1 rounded-full transition-all duration-500 ${
                        currentStepIndex > index ? 'bg-indigo-500' : 'bg-gray-200'
                      }`}
                    />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        {currentStep === 1 && (
          <StepNameCategory formData={formData} updateFormData={updateFormData} />
        )}
        {currentStep === 2 && (
          <StepLocation formData={formData} updateFormData={updateFormData} />
        )}
        {currentStep === 3 && (
          <StepDescription formData={formData} updateFormData={updateFormData} />
        )}
        {currentStep === 3.5 && (
          <StepGoodWork formData={formData} updateFormData={updateFormData} />
        )}
        {currentStep === 4 && (
          <StepDetails formData={formData} updateFormData={updateFormData} />
        )}
        {currentStep === 5 && (
          <StepVisibility formData={formData} updateFormData={updateFormData} />
        )}
      </div>

      {/* Footer */}
      <div className="sticky bottom-0 bg-white border-t border-gray-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between">
            <button
              onClick={handleBack}
              className="px-6 py-3 text-gray-700 font-semibold hover:bg-gray-100 rounded-lg transition"
            >
              Zurueck
            </button>
            {!isLastStep ? (
              <button
                onClick={handleNext}
                disabled={!isStepValid(currentStep)}
                className="px-6 py-3 bg-indigo-500 text-white font-semibold rounded-lg hover:bg-indigo-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Weiter
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={!isStepValid(currentStep) || isSubmitting}
                className="px-6 py-3 bg-indigo-500 text-white font-semibold rounded-lg hover:bg-indigo-600 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    {formData.visibility === 'private' ? 'Wird erstellt...' : 'Wird eingereicht...'}
                  </>
                ) : (
                  formData.visibility === 'private' ? 'Privaten Ort erstellen' : 'Ort vorschlagen'
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PlaceWizard;
